import React, { useCallback, useEffect, useMemo, useRef, useState, type Dispatch } from "react";
import { fetchRentalDetails, fetchRentalPage, fetchRentals, fetchRentalsById, canonicalLikedIds, pricePerMonthInBaseCurrency, RENTAL_LIST_PAGE_SIZE, sortRentals, type Rental } from "../lib/rental";
import useStore from "../lib/store";
import type { Filters, Status } from "../lib/consts";
import { coordsEqual } from "../lib/utils";

type DetailHydrationStatus = 'idle' | 'loading' | 'loaded' | 'error';

export default function useRentals(setStatus: Dispatch<React.SetStateAction<Status>>, filters: Filters,
    keywords: string,
    minPostedAt: Date,
    useMapBounds: boolean,
    waitForMapScope: boolean): {
        listRentals: Rental[],
        hasMore: boolean,
        loadingMore: boolean,
        loadMore: () => void,
        totalMatches: number | null,
        errorMessage?: string,
    } {

    const rentals = useStore((state) => state.rentals);
    const setRentals = useStore((state) => state.setRentals);
    const appendRentals = useStore((state) => state.appendRentals);
    const mergeRentals = useStore((state) => state.mergeRentals);
    const likedRentalIds = useStore((state) => state.likedRentalIds);
    const showLikedOnly = useStore((state) => state.showLikedOnly);
    const [likedRentals, setLikedRentals] = useState<Rental[]>();
    const { selectedLocation, bounds: mapBounds, locations } = useStore((state) => state.map);
    const [rentalsLoaded, setRentalsLoaded] = useState(false);
    const [listStatus, setListStatus] = useState<Status>('idle');
    const [detailHydrationStatus, setDetailHydrationStatus] = useState<DetailHydrationStatus>('idle');
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalMatches, setTotalMatches] = useState<number | null>(null);
    const [visibleSortedCount, setVisibleSortedCount] = useState(RENTAL_LIST_PAGE_SIZE);
    const loadGenerationRef = useRef(0);
    const hydratedRentalIdsRef = useRef(new Set<string>());
    const hasKeywords = keywords.trim().length > 0;
    const isPriceSorted = filters.sort !== 'none';
    const requestedLocation = selectedLocation || filters.location.trim();
    const rentalQueryFilters = useMemo(() => ({
        location: '',
        price_range: '',
        keywords: '',
        min_bedrooms: filters.min_bedrooms,
        dateRange: filters.dateRange,
        sort: 'none',
    } as Filters), [filters.min_bedrooms, filters.dateRange]);
    const requestedLocationNamesInScope = useMemo(() => {
        if (!requestedLocation) return null;
        if (!locations) return [requestedLocation];

        const selectedCoords = locations.get(requestedLocation.toLowerCase());
        if (!selectedCoords) return [requestedLocation];

        return Array.from(locations.values())
            .filter(location => coordsEqual(selectedCoords, location))
            .map(location => location.name)
            .sort();
    }, [locations, requestedLocation]);

    const mapLocationNamesInScope = useMemo(() => {
        if (requestedLocation || !useMapBounds) return null;
        if (!mapBounds) return null;
        if (!locations) return null;

        const { ne, sw } = mapBounds;
        return Array.from(locations.values())
            .filter(location => location.lat <= ne.lat
                && location.lng <= ne.lng
                && location.lat >= sw.lat
                && location.lng >= sw.lng)
            .map(location => location.name)
            .sort();
    }, [locations, mapBounds, requestedLocation, useMapBounds]);
    const locationNamesInScope = requestedLocationNamesInScope || mapLocationNamesInScope;
    useEffect(() => {
        if (!rentalsLoaded) return;
        let cancelled = false;
        const loadedRentals = rentals.filter(rental => likedRentalIds.includes(rental.id));
        const missingIds = likedRentalIds.filter(id => !loadedRentals.some(rental => rental.id === id));
        const loadLikes = async () => {
            try {
                const resolved = await fetchRentalsById(missingIds);
                if (cancelled) return;
                const aliases = new Map(resolved.map(({ requestedId, rental }) => [requestedId, rental.id]));
                const ids = canonicalLikedIds(likedRentalIds, aliases);
                if (ids.length !== likedRentalIds.length || ids.some((id, index) => id !== likedRentalIds[index])) {
                    useStore.getState().setLikedRentalIds(ids);
                }
                const byId = new Map([...loadedRentals, ...resolved.map(row => row.rental)].map(r => [r.id, r]));
                setLikedRentals(ids.map(id => byId.get(id)).filter((r): r is Rental => Boolean(r)).reverse());
            } catch (error) {
                console.error('Error fetching missing liked rentals:', error);
            }
        };
        loadLikes();
        return () => { cancelled = true; };
    }, [rentals, rentalsLoaded, likedRentalIds]);

    useEffect(() => {
        const generation = loadGenerationRef.current + 1;
        loadGenerationRef.current = generation;
        hydratedRentalIdsRef.current = new Set();
        setListStatus('loading');
        setDetailHydrationStatus('idle');
        setRentalsLoaded(false);
        setLoadingMore(false);
        setVisibleSortedCount(RENTAL_LIST_PAGE_SIZE);

        if (waitForMapScope) return;

        const scope = { locationNames: locationNamesInScope, minPostedAt };
        // Prices need monthly normalization and exchange rates, so load the full
        // scope before sorting in the browser and revealing it in list-sized pages.
        const request = isPriceSorted
            ? fetchRentals(rentalQueryFilters, scope).then(rentals => ({
                rentals, hasMore: false, totalCount: rentals.length,
            }))
            : fetchRentalPage(rentalQueryFilters, scope);
        request.then(page => {
            if (loadGenerationRef.current !== generation) return;
            setRentals(page.rentals);
            setHasMore(page.hasMore);
            setTotalMatches(page.totalCount);
            setListStatus('loaded');
            setRentalsLoaded(true);
            if (page.rentals.length === 0) {
                setDetailHydrationStatus('loaded');
            }
        }).catch((error) => {
            if (loadGenerationRef.current !== generation) return;
            console.error('Error fetching rentals:', error);
            setListStatus('error');
            setDetailHydrationStatus('idle');
        });
    }, [isPriceSorted, locationNamesInScope, minPostedAt, rentalQueryFilters, setRentals, waitForMapScope]);

    useEffect(() => {
        setVisibleSortedCount(RENTAL_LIST_PAGE_SIZE);
    }, [filters.sort, filters.price_range, keywords]);

    const loadMore = useCallback(() => {
        if (loadingMore || listStatus !== 'loaded') return;
        if (isPriceSorted) {
            setVisibleSortedCount(count => count + RENTAL_LIST_PAGE_SIZE);
            return;
        }
        if (!hasMore) return;

        const generation = loadGenerationRef.current;
        setLoadingMore(true);
        fetchRentalPage(rentalQueryFilters, {
            offset: useStore.getState().rentals.length,
            locationNames: locationNamesInScope,
            minPostedAt,
        }).then(page => {
            if (loadGenerationRef.current !== generation) return;
            appendRentals(page.rentals);
            setHasMore(page.hasMore);
            setTotalMatches(page.totalCount);
        }).catch(error => {
            if (loadGenerationRef.current !== generation) return;
            console.error('Error loading more rentals:', error);
        }).finally(() => {
            if (loadGenerationRef.current !== generation) return;
            setLoadingMore(false);
        });
    }, [appendRentals, hasMore, isPriceSorted, listStatus, loadingMore, locationNamesInScope, minPostedAt, rentalQueryFilters]);

    useEffect(() => {
        if (!rentalsLoaded || !hasKeywords) return;

        const generation = loadGenerationRef.current;
        const idsToHydrate = rentals
            .filter(rental => !hydratedRentalIdsRef.current.has(rental.id))
            .map(rental => rental.id);
        if (idsToHydrate.length === 0) {
            setDetailHydrationStatus('loaded');
            return;
        }

        idsToHydrate.forEach(id => hydratedRentalIdsRef.current.add(id));
        setDetailHydrationStatus('loading');
        fetchRentalDetails(idsToHydrate).then(details => {
            if (loadGenerationRef.current !== generation) return;
            mergeRentals(details);
            setDetailHydrationStatus('loaded');
        }).catch(error => {
            if (loadGenerationRef.current !== generation) return;
            idsToHydrate.forEach(id => hydratedRentalIdsRef.current.delete(id));
            console.error('Error hydrating rental details:', error);
            setDetailHydrationStatus('error');
        });
    }, [hasKeywords, mergeRentals, rentals, rentalsLoaded]);

    useEffect(() => {
        if (listStatus !== 'loaded') {
            setStatus(listStatus);
            return;
        }

        if (hasKeywords && detailHydrationStatus === 'loading') {
            setStatus('loading');
            return;
        }

        if (hasKeywords && detailHydrationStatus === 'error') {
            setStatus('error');
            return;
        }

        setStatus('loaded');
    }, [detailHydrationStatus, hasKeywords, listStatus, setStatus]);

    const exchangeRates = useStore((state) => state.exchangeRates);
    const canApplyKeywordFilter = !hasKeywords || detailHydrationStatus === 'loaded';

    /** Rentals filtered by price and keywords, but not map bounds or selected location. */
    const filteredRentals = useMemo(() => {
        const keywordTokens = keywords.replace(',', '').split(/\s+/).filter(Boolean);
        const priceMax = filters.price_range ? Number(filters.price_range) * 1000000 : Infinity;

        return rentals.filter((rental) => {
            const text = ((rental.title || '') + ' ' + (rental.raw_description || '')).toLowerCase();
            if (canApplyKeywordFilter && !keywordTokens.every(token => text.includes(token))) return false;

            if (isFinite(priceMax)) {
                const priceInIDR = pricePerMonthInBaseCurrency(rental, exchangeRates);
                if (priceInIDR !== null && priceInIDR > priceMax) return false;
            }

            return true;
        });
    }, [rentals, keywords, canApplyKeywordFilter, filters.price_range, exchangeRates]);

    /** Further filtered by explicitly selected location for the rental list panel. */
    const listRentals = useMemo(() => {
        if (!selectedLocation) return sortRentals(filteredRentals, filters.sort, exchangeRates);
        const selectedCoords = locations?.get(selectedLocation.toLowerCase());
        const filtered = filteredRentals.filter(rental => {
            const coords = locations?.get((rental.location as string).toLowerCase());
            return coords && coordsEqual(selectedCoords, coords);
        });
        return sortRentals(filtered, filters.sort, exchangeRates);
    }, [filteredRentals, selectedLocation, locations, filters.sort, exchangeRates]);

    const liked = showLikedOnly ? likedRentals?.slice().reverse() || [] : undefined;
    const errorMessage = hasKeywords && detailHydrationStatus === 'error'
        ? 'Could not load listing details for keyword search.'
        : undefined;
    return {
        listRentals: liked || (isPriceSorted ? listRentals.slice(0, visibleSortedCount) : listRentals),
        hasMore: !showLikedOnly && (isPriceSorted ? listRentals.length > visibleSortedCount : hasMore),
        loadingMore,
        loadMore,
        totalMatches: showLikedOnly ? null : totalMatches,
        errorMessage,
    };
}
